import { memo } from 'react';

/**
 * Juego de posición (positional play) grid overlay for the 2D
 * tactics board. Builds on the half-spaces overlay by adding two
 * horizontal lines splitting the pitch into thirds, plus a highlighted
 * **zone-14** rectangle in the centre attacking third (the canonical
 * "danger zone" — the centre-of-attacking-third zone numbered 14 on
 * the 18-zone tactical grid).
 *
 * Coordinate convention: same landscape mapping as every other 2D
 * layer (`SVG-x = -world.z`, `SVG-y = world.x`). Horizontal lines in
 * WORLD z (constant z) become vertical lines in SVG (constant svg-x).
 */

const FIELD_LENGTH_M = 105;
const FIELD_WIDTH_M = 68;
const HALF_W = FIELD_WIDTH_M / 2;
const HALF_L = FIELD_LENGTH_M / 2;
const THIRD_L = FIELD_LENGTH_M / 3;

/** World-z positions of the two horizontal lines dividing the pitch
 *  into defending / middle / attacking thirds. */
const HORIZONTAL_LINE_ZS = [
  -HALF_L + THIRD_L,         // -17.5
  -HALF_L + THIRD_L * 2,     //  17.5
];

const STROKE_HEX = '#fbbf24';

/** Zone-14 — the canonical "dangerous zone" in tactical literature.
 *  Centre channel (one half-space wide, FIELD_WIDTH/5 ≈ 13.6 m), in
 *  the attacking third. Highlighted with a tinted fill + stronger
 *  outline so it pops on the rest of the grid. */
const ZONE14_HALF_WIDTH = FIELD_WIDTH_M / 10;       // ±6.8 m (centre channel)
const ZONE14_Z_MIN = -HALF_L + THIRD_L * 2;          //  17.5 (start of attacking third)
const ZONE14_Z_MAX = HALF_L;                         //  52.5 (goal line)

export interface PositionalPlayGridLayer2DProps {
  /** Opacity in [0, 1]. */
  opacity: number;
}

function PositionalPlayGridLayer2DInner({
  opacity,
}: PositionalPlayGridLayer2DProps) {
  const stroke = `rgba(251, 191, 36, ${opacity})`;
  const zone14Fill = `rgba(251, 191, 36, ${0.12 * opacity})`;
  const zone14Stroke = `rgba(251, 191, 36, ${Math.min(1, opacity + 0.2)})`;
  return (
    <g
      className="t2d-posplay-grid-layer"
      data-testid="t2d-posplay-grid-layer"
      pointerEvents="none"
      clipPath="url(#t2d-pitch-clip)"
    >
      {/* Two horizontal lines dividing the pitch into thirds. Same
          dashed-yellow treatment as half-spaces but oriented across
          the pitch. World z at constant value, x ∈ [-halfW, halfW]
          → SVG segment at constant svg-x, y ∈ [-halfW, halfW]. */}
      {HORIZONTAL_LINE_ZS.map((worldZ, idx) => (
        <line
          key={`third-${idx}`}
          x1={-worldZ}
          y1={-HALF_W}
          x2={-worldZ}
          y2={HALF_W}
          stroke={stroke}
          strokeWidth={0.18}
          strokeDasharray="1.4 1.0"
          strokeLinecap="round"
          data-testid={`t2d-posplay-third-${idx}`}
        />
      ))}
      {/* Zone-14 rectangle: centre channel × attacking third. World
          (x ∈ [-halfW/5, +halfW/5], z ∈ [z14_min, z14_max]) →
          SVG (svg-x ∈ [-z14_max, -z14_min], svg-y ∈ [-halfW/5, halfW/5]). */}
      <rect
        x={-ZONE14_Z_MAX}
        y={-ZONE14_HALF_WIDTH}
        width={ZONE14_Z_MAX - ZONE14_Z_MIN}
        height={ZONE14_HALF_WIDTH * 2}
        fill={zone14Fill}
        stroke={zone14Stroke}
        strokeWidth={0.22}
        strokeDasharray="2.0 1.4"
        data-testid="t2d-posplay-zone14"
      />
    </g>
  );
}

export const PositionalPlayGridLayer2D = memo(PositionalPlayGridLayer2DInner);

export const POSPLAY_HORIZONTAL_LINE_ZS = HORIZONTAL_LINE_ZS;
export const POSPLAY_ZONE14 = {
  halfWidth: ZONE14_HALF_WIDTH,
  zMin: ZONE14_Z_MIN,
  zMax: ZONE14_Z_MAX,
};
export const POSPLAY_STROKE_HEX = STROKE_HEX;
